import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import { Word } from './word'

/**
 * @example
 * // Convey the type of item in its id
 * export type TypedId<TType extends Type = Type, TPartialId extends PartialId = PartialId> = `${TType}:${TPartialId}`
 * // Support multiple versions of item
 * export type VersionedId<TPartialId extends PartialId = PartialId, TVersion extends number = number> = `${TPartialId}/${TVersion}`
 * @deprecated
 *
 * __NOTE:__ this is no longer needed with the introduction of Quiver
 *
 * Represents the core part of an ID, but can be encoded in more complete ID
 * types to include more information
 *
 * A common use case is to have some system-controlled items and some
 * user-controlled items. To support this use case, we have
 * {@link PartialId.Standard} for system-controlled items. and
 * {@link PartialId.Custom} for user-controlled items.
 */
export type PartialId<TSuffix extends Word = Word> =
  | PartialId.Standard<TSuffix>
  | PartialId.Custom<TSuffix>
export namespace PartialId {
  export const isPartialId: TypeChecker<PartialId> = TypeChecker.Factory.lazy(
    () => TypeChecker.Factory.union(Standard.isStandard, Custom.isCustom),
  )
  export const assertIsPartialId: TypeAsserter<PartialId> =
    TypeAsserter.Factory.from(isPartialId)

  /**
   * Extracts the suffix of the given {@link PartialId}
   */
  export function getSuffix<TSuffix extends Word>(
    partialId: PartialId<TSuffix>,
  ): TSuffix {
    if (Standard.isStandard(partialId)) {
      return partialId.substring(Standard.PREFIX.length) as TSuffix
    }
    if (Custom.isCustom(partialId)) {
      return partialId.substring(Custom.PREFIX.length) as TSuffix
    }
    throw TypeAsserter.assertIsNever(partialId)
  }

  /**
   * Coerces the given value into a {@link PartialId}
   *
   * If a {@link PartialId} is given, then it is returned as-is. Otherwise, a
   * {@link PartialId.Custom} is created with the given value as a suffix.
   */
  export function from<TPartialIdOrSuffix extends PartialId | Word | undefined>(
    partialIdOrSuffix: TPartialIdOrSuffix,
  ): TPartialIdOrSuffix extends PartialId
    ? TPartialIdOrSuffix
    : PartialId.Custom<
        TPartialIdOrSuffix extends undefined ? Word : TPartialIdOrSuffix
      >
  // NOTE: overload is just used to avoid problems due to type parameters
  export function from(
    partialIdOrSuffix: PartialId | Word | undefined,
  ): PartialId {
    return PartialId.isPartialId(partialIdOrSuffix)
      ? partialIdOrSuffix
      : Custom.create(partialIdOrSuffix)
  }

  /**
   * Can be used to represent and ID that is standard to our system
   *
   * This is in contrast to {@link Custom}
   *
   * @see {@link PartialId}
   */
  export type Standard<TSuffix extends Word = Word> =
    `${Standard.Prefix}${TSuffix}`
  export namespace Standard {
    export const PREFIX = '!'
    export type Prefix = typeof PREFIX

    /**
     * Produces a {@link TypeChecker} for a {@link Standard} {@link PartialId}
     * that has a desired suffix
     */
    export function getTypeChecker<TSuffix extends Word>(
      isSuffix: TypeChecker<TSuffix>,
    ): TypeChecker<Standard<TSuffix>> {
      return _getTypeChecker(PREFIX, isSuffix)
    }

    export const isStandard: TypeChecker<Standard> = getTypeChecker(Word.isWord)
    export const assertIsStandard: TypeAsserter<Standard> =
      TypeAsserter.Factory.from(isStandard)

    /**
     * Creates a {@link Standard} with the given suffix
     */
    export function create<TSuffix extends Word>(
      suffix: TSuffix,
    ): Standard<TSuffix> {
      return `${PREFIX}${suffix}`
    }
  }

  /**
   * Can be used to represent and ID that is **not** standard to our system
   *
   * This is in contrast to {@link Standard}
   *
   * @see {@link PartialId}
   */
  export type Custom<TSuffix extends Word = Word> = `${Custom.Prefix}${TSuffix}`
  export namespace Custom {
    export const PREFIX = '?'
    export type Prefix = typeof PREFIX

    /**
     * Produces a {@link TypeChecker} for a {@link Custom} {@link PartialId}
     * that has a desired suffix
     */
    export function getTypeChecker<TSuffix extends Word>(
      isSuffix: TypeChecker<TSuffix>,
    ): TypeChecker<Custom<TSuffix>> {
      return _getTypeChecker(PREFIX, isSuffix)
    }

    export const isCustom: TypeChecker<Custom> = getTypeChecker(Word.isWord)
    export const assertIsCustom: TypeAsserter<Custom> =
      TypeAsserter.Factory.from(isCustom)

    /**
     * Creates a {@link Custom} with the given suffix, or a randomly generated
     * suffix if one is not provided
     */
    export function create<TSuffix extends Word>(
      suffix: TSuffix,
    ): Custom<TSuffix>
    export function create(suffix?: Word): Custom
    export function create(suffix: Word = Word.generate()): Custom {
      return `${PREFIX}${suffix}`
    }
  }
}

/**
 * NOTE: there may be cases where this will not work properly if the prefix is
 * more than ONE character long
 */
type AnyPrefix = PartialId.Standard.Prefix | PartialId.Custom.Prefix
type PartialIdWithPrefix<
  TPrefix extends AnyPrefix,
  TSuffix extends Word,
> = TPrefix extends PartialId.Standard.Prefix
  ? PartialId.Standard<TSuffix>
  : TPrefix extends PartialId.Custom.Prefix
    ? PartialId.Custom<TSuffix>
    : PartialId<TSuffix>

/**
 * Used to produce a {@link TypeChecker} for {@link PartialId} or one of its
 * subtypes
 */
function _getTypeChecker<TPrefix extends AnyPrefix, TSuffix extends Word>(
  prefix: TPrefix,
  isSuffix: TypeChecker<TSuffix>,
): TypeChecker<PartialIdWithPrefix<TPrefix, TSuffix>> {
  let genericType = 'PartialId'
  if (prefix === PartialId.Standard.PREFIX) {
    genericType += '.Standard'
  } else if (prefix === PartialId.Custom.PREFIX) {
    genericType += '.Custom'
  }
  return TypeChecker.Factory.custom(
    (val: unknown): val is PartialIdWithPrefix<TPrefix, TSuffix> =>
      TypeChecker.isString(val) &&
      val.startsWith(prefix) &&
      isSuffix(val.substring(prefix.length)),
    {
      /**
       * Getter handles getter in {@link isSuffix.typeDescription}
       */
      get typeDescription() {
        return `PartialId<${isSuffix.typeDescription}>`
      },
    },
  )
}
